import { z } from 'zod';
import { apiAdminboostersPostBodyTitleMax } from '@r/api/generated-zod/index.ts';
import { toUtcEndOfDay, toUtcStartOfDay } from '@r/utils/date-utils.ts';
import type {
  BoosterBoosterWriteJsonMergePatchModel,
  BoosterBoosterWriteModel,
  BoosterReferenceBoosterWriteModel
} from '@r/api/generated/client.schemas.ts';
import {
  areBoosterReferencesWriteModelsEqual,
  buildBoosterReferencesWriteModel,
  type BoosterReferenceTierMap
} from './booster-reference-tiers.ts';

// Coefficient global minimum imposé côté produit à l'étape 1 (les paliers API acceptent 0).
const GLOBAL_COEFFICIENT_MIN = 2;

const getBoosterFieldsSchema = (t: (key: string) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t('boosters.create.title.required'))
      .max(apiAdminboostersPostBodyTitleMax, t('boosters.create.title.max')),
    description: z.string(),
    startDate: z.date({
      required_error: t('boosters.create.start.date.required')
    }),
    endDate: z.date({
      required_error: t('boosters.create.end.date.required')
    }),
    coefficient: z
      .number()
      .int(t('boosters.create.coefficient.integer'))
      .min(GLOBAL_COEFFICIENT_MIN, t('boosters.create.coefficient.min'))
  });

/** Schéma Zod du formulaire de création, aligné sur les contraintes du POST /admin/boosters. */
export const getCreateBoosterSchema = (t: (key: string) => string) =>
  getBoosterFieldsSchema(t).refine((data) => data.endDate > data.startDate, {
    message: t('boosters.create.dates.invalid'),
    path: ['endDate']
  });

export type BoosterDraftData = z.infer<ReturnType<typeof getCreateBoosterSchema>>;

/** Normalise la description formulaire vers le format nullable attendu par l'API. */
function normalizeBoosterDescription(description: string) {
  const trimmedDescription = description.trim();
  return trimmedDescription === '' ? null : trimmedDescription;
}

/** Construit un PATCH ne contenant que les champs réellement modifiés et autorisés. */
export function toBoosterPatchModel(
  original: BoosterDraftData,
  updated: BoosterDraftData,
  originalBoosterReferences: BoosterReferenceBoosterWriteModel[],
  updatedReferenceIds: number[],
  updatedCustomTiersByReferenceId: BoosterReferenceTierMap,
  hasStarted: boolean
): BoosterBoosterWriteJsonMergePatchModel {
  const patch: BoosterBoosterWriteJsonMergePatchModel = {};

  if (updated.title !== original.title) {
    patch.title = updated.title;
  }

  if (updated.description !== original.description) {
    patch.description = normalizeBoosterDescription(updated.description);
  }

  if (hasStarted) {
    return patch;
  }

  const originalStartDate = toUtcStartOfDay(original.startDate).toISOString();
  const updatedStartDate = toUtcStartOfDay(updated.startDate).toISOString();
  if (updatedStartDate !== originalStartDate) {
    patch.startDate = updatedStartDate;
  }

  const originalEndDate = toUtcEndOfDay(original.endDate).toISOString();
  const updatedEndDate = toUtcEndOfDay(updated.endDate).toISOString();
  if (updatedEndDate !== originalEndDate) {
    patch.endDate = updatedEndDate;
  }

  const updatedBoosterReferences = buildBoosterReferencesWriteModel(
    updatedReferenceIds,
    updatedCustomTiersByReferenceId,
    updated.coefficient
  );

  if (!areBoosterReferencesWriteModelsEqual(originalBoosterReferences, updatedBoosterReferences)) {
    patch.boosterReferences = updatedBoosterReferences;
  }

  return patch;
}

/** Transforme le brouillon UI (dates locales) en payload API avec paliers par référence. */
export function toBoosterWriteModel(
  draft: BoosterDraftData,
  referenceIds: number[],
  customTiersByReferenceId: BoosterReferenceTierMap = {}
): BoosterBoosterWriteModel {
  return {
    title: draft.title,
    description: normalizeBoosterDescription(draft.description),
    startDate: toUtcStartOfDay(draft.startDate).toISOString(),
    endDate: toUtcEndOfDay(draft.endDate).toISOString(),
    boosterReferences: buildBoosterReferencesWriteModel(
      referenceIds,
      customTiersByReferenceId,
      draft.coefficient
    )
  };
}
